import { createApi } from '@reduxjs/toolkit/query/react';
import axios, { AxiosProgressEvent, CancelTokenSource } from 'axios';

import { axiosBaseQuery } from '../axios/axiosBaseQuery';
import { DocumentCategoriesReply } from './dtos/DocumentCategoriesReply';
import { DocumentFileDto } from './dtos/DocumentFileDto';
import { DocumentFilesReply } from './dtos/DocumentFilesReply';
import { DocumentCategory } from './models/DocumentCategory';
import { DocumentFile } from './models/DocumentFile';
import { mapDocumentFileDtoToModel } from './utils/mapDocumentFileDtoToModel';
import { transformDocumentCategoriesReply } from './utils/transformDocumentCategoriesReply';
import { transformDocumentFilesReply } from './utils/transformDocumentFilesReply';

export interface FetchDocumentFilesParams {
  name?: string;
  startDate?: string;
  endDate?: string;
  category?: DocumentCategory;
}

interface UploadDocumentFileParams {
  file: File;
  name?: string;
  category?: string;
  onProgress?: (event: AxiosProgressEvent) => void;
  onReceivedCancelToken?: (cancelToken: CancelTokenSource) => void;
}

export const documentArchiveApi = createApi({
  reducerPath: 'documentArchiveApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['DocumentFiles'],
  endpoints: (builder) => ({
    documentCategories: builder.query<DocumentCategory[], void>({
      query: () => ({
        url: `${window.config.connectBaseApiUrl}/api/documents/categories`,
      }),
      transformResponse: (dto: DocumentCategoriesReply) => {
        return transformDocumentCategoriesReply(dto);
      },
    }),

    documentFiles: builder.query<DocumentFile[], FetchDocumentFilesParams>({
      query: ({ name, startDate, endDate, category }) => ({
        url: `${window.config.connectBaseApiUrl}/api/documents`,
        params: {
          name: name,
          startDate: startDate,
          endDate: endDate,
          category: category,
        },
      }),
      providesTags: ['DocumentFiles'],
      keepUnusedDataFor: 0,
      transformResponse: (dto: DocumentFilesReply) => {
        return transformDocumentFilesReply(dto);
      },
    }),

    uploadDocumentFile: builder.mutation<
      DocumentFile,
      UploadDocumentFileParams
    >({
      query: ({ file, name, category, onProgress, onReceivedCancelToken }) => {
        /** Get a new cancel token for this upload */
        const cancelToken = axios.CancelToken.source();
        if (onReceivedCancelToken) {
          onReceivedCancelToken(cancelToken);
        }

        /** Build form data for file upload */
        const formData = new FormData();
        formData.append('file', file, file.name);
        if (name) {
          formData.append('name', name);
        }
        if (category) {
          formData.append('category', category);
        }

        return {
          url: `${window.config.connectBaseApiUrl}/api/documents`,
          method: 'POST',
          data: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 600000,
          onUploadProgress: onProgress || null,
          cancelToken: cancelToken?.token,
        };
      },
      transformResponse: (dto: DocumentFileDto[]) => {
        return mapDocumentFileDtoToModel(dto[0]);
      },
      invalidatesTags: ['DocumentFiles'],
    }),

    deleteDocumentFile: builder.mutation<void, string>({
      query: (fileId) => ({
        url: `${window.config.connectBaseApiUrl}/api/documents/${fileId}`,
        method: 'DELETE',
      }),
      /**
       * Optimistic delete: the document lives in MService-Document and the
       * backend deletes it asynchronously. If we relied on cache invalidation
       * (refetching the list after the DELETE response), the document would
       * reappear because MService-Document hasn't processed the deletion yet.
       *
       * Instead we immediately remove the document from every cached query result
       * so the UI stays consistent. If the DELETE request itself fails, the
       * patches are rolled back and the document reappears.
       */
      async onQueryStarted(fileId, { dispatch, queryFulfilled, getState }) {
        const entriesToUpdate = documentArchiveApi.util.selectInvalidatedBy(
          getState(),
          [{ type: 'DocumentFiles' }],
        );

        const patchResults = entriesToUpdate
          .filter((entry) => entry.endpointName === 'documentFiles')
          .map(({ originalArgs }) =>
            dispatch(
              documentArchiveApi.util.updateQueryData(
                'documentFiles',
                originalArgs,
                (draft) => draft.filter((doc) => doc.id !== fileId),
              ),
            ),
          );

        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((patch) => patch.undo());
        }
      },
    }),
  }),
});

export const {
  useDocumentCategoriesQuery,
  useDocumentFilesQuery,
  useUploadDocumentFileMutation,
  useDeleteDocumentFileMutation,
  util,
} = documentArchiveApi;
